import { matchPath } from 'react-router-dom'

const APP_TITLE = 'Numeo'

/**
 * Browser-tab title of every page, keyed by route pattern. The tab reads `<title> | Numeo`.
 *
 * The first matching entry wins and every pattern matches the WHOLE path, so a nested page lists its own
 * entry (`/accounting/driver-pay/:statementId`) instead of inheriting its parent's. A page missing here gets
 * the bare `Numeo`, never a neighbour's name. Names follow the sidebar (`layouts/components/sidebar/menuConfig.tsx`);
 * where a sidebar label repeats across sections (Dashboard, SMS, Analytics) the title carries the section.
 */
const ROUTE_TITLES: ReadonlyArray<readonly [pattern: string, title: string]> = [
    // Dashboard / Home
    ['/dispatcher', 'Dashboard'],
    ['/spot', 'Dashboard'],
    ['/onboarding/tms/welcome', 'Home'],
    ['/accounting/pl-report', 'P&L Dashboard'],
    ['/truck-availability-report', 'Truck Availability'],
    ['/updater-agent/task-management', 'Task Management'],
    ['/updater-agent/broker-performance', 'Broker Performance'],
    ['/accounting/1099-reports', '1099 Reports'],
    ['/accounting/truck-pl', 'Truck P&L'],
    ['/accounting/truck-pl/:truckId', 'Truck P&L Details'],

    // Find Loads
    ['/dispatcher/load-hub', 'Load Hub'],
    ['/dispatcher/load-radar', 'Load Radar'],
    ['/dispatcher/ai-hub', 'AI Hub'],
    ['/dispatcher/ai-hub/bookings', 'Booking History'],
    ['/dispatcher/dispatch-help', 'Dispatch Help'],
    ['/dispatcher/general-agent', 'General Agent'],
    ['/dispatcher/analytics', 'Analytics'],
    ['/dispatcher/leaderboard', 'Dispatcher Leaderboard'],
    ['/dispatcher/communications', 'Communications'],
    ['/dispatcher/faq', 'FAQ'],
    ['/dispatcher/settings', 'Settings'],

    // Box Truck
    ['/dispatcher/box-truck-loadboard', 'Box Truck Loadboard'],
    ['/dispatcher/box-truck-bidding', 'Box Truck Bidding'],
    ['/dispatcher/box-truck-my-drivers', 'Box Truck My Drivers'],
    ['/dispatcher/box-truck-hr-my-drivers', 'Box Truck My Drivers'],
    ['/dispatcher/box-truck-driver-verifications', 'Driver Verifications'],
    ['/dispatcher/box-truck-map', 'Box Truck Map'],
    ['/dispatcher/box-truck-mailing', 'Box Truck Mailing'],
    ['/dispatcher/box-truck-sms', 'Box Truck SMS'],
    ['/dispatcher/box-truck-analytics', 'Box Truck Analytics'],

    // Manage Loads
    ['/updater-agent', 'Fleet View'],
    ['/updater-agent/scheduler', 'Dispatch Board'],
    ['/updater-agent/scheduler/leaderboard', 'Dispatcher Leaderboard'],
    ['/updater-agent/loads', 'Ongoing Loads'],
    ['/updater-agent/loads/:loadNumber', 'Load Details'],

    // Accounting
    ['/accounting/invoices', 'Invoices (AR)'],
    ['/accounting/driver-pay', 'Driver Pay'],
    ['/accounting/driver-pay/batches/:batchId', 'Driver Pay Batch'],
    ['/accounting/driver-pay/:statementId', 'Driver Pay Statement'],
    ['/accounting/contractor-pay', 'Contractor Pay'],
    ['/accounting/contractor-pay/:statementId', 'Contractor Pay Statement'],
    ['/accounting/fuel-toll', 'Fuel & Toll'],
    ['/accounting/expenses-adjustments', 'Expenses & Adjustments'],
    ['/accounting/pay-adjustments', 'Pay Adjustments'],
    ['/accounting/factoring', 'Factoring'],
    ['/instant-pay', 'Instant Pay'],

    // Fleet Management
    ['/fleet/overview', 'Fleet Overview'],
    ['/fleet/compliance', 'Fleet Compliance'],
    ['/fleet/maintenance', 'Maintenance'],
    ['/fleet/inspections', 'Inspections'],
    ['/fuel-ai', 'Fuel AI'],

    // Safety
    ['/safety/dashboard', 'Safety Dashboard'],
    ['/safety/driver-compliance', 'Driver Compliance'],
    ['/safety/dot-inspections', 'DOT Inspections'],
    ['/safety/accidents', 'Accidents & Claims'],
    ['/safety/drug-alcohol', 'Drug & Alcohol'],
    ['/safety/ifta', 'IFTA'],
    ['/safety/ifta/:id', 'IFTA Report'],
    ['/safety/company-files', 'Company Files'],

    // Profiles
    ['/profiles/drivers', 'Drivers'],
    ['/profiles/contractors', 'Contractors'],
    ['/profiles/assets/trucks', 'Trucks'],
    ['/profiles/assets/trucks/manage', 'Manage Truck'],
    ['/profiles/assets/trailers', 'Trailers'],
    ['/profiles/brokers', 'Brokers'],

    // Market Intel
    ['/market-intel/price', 'Market Intel Price'],
    ['/market-intel/load-count', 'Market Intel Load Count'],

    // Account
    ['/settings', 'Settings'],
    ['/billing', 'Billing'],
    ['/help-center', 'Help Center'],
    ['/faq', 'FAQ'],
    ['/emails', 'Emails'],
    ['/identity-protection', 'Identity Protection'],
    ['/call-flow', 'Call Flow'],
    ['/call-flow/new', 'New Call Flow'],
    ['/call-flow/:id/edit', 'Edit Call Flow'],

    // Sign-in
    ['/auth/sign-in', 'Sign In'],
    ['/auth/forgot-password', 'Forgot Password'],
    ['/auth/reset-password', 'Reset Password'],
    ['/auth/terms', 'Terms of Service'],
    ['/auth/privacy-policy', 'Privacy Policy'],
    ['/auth/onboarding', 'Get Started'],
    ['/auth/invite/:id', 'Accept Invite'],
    ['/auth/complete-signup', 'Complete Sign Up'],
    ['/auth/gmail/callback', 'Connecting Gmail'],
    ['/auth/google-gmail/callback', 'Connecting Gmail'],
    ['/auth/microsoft/callback', 'Connecting Microsoft'],
    ['/auth/nylas/callback', 'Connecting Email'],
    ['/auth/samsara/callback', 'Connecting Samsara'],
    ['/auth/motive/callback', 'Connecting Motive'],
    ['/auth/hub', 'Sign In'],
    ['/auth/hub/set-password', 'Set Password'],
    ['/auth/hub/forgot-password', 'Forgot Password'],
    ['/auth/tms', 'Sign In'],
    ['/auth/tms/set-password', 'Set Password'],
    ['/auth/tms/forgot-password', 'Forgot Password'],

    // Onboarding
    ['/onboarding', 'Get Started'],
    ['/onboarding/verify', 'Verify Email'],
    ['/onboarding/create-password', 'Create Password'],
    ['/onboarding/login', 'Sign In'],
    ['/onboarding/forgot-password', 'Forgot Password'],
    ['/onboarding/install', 'Install Extension'],
    ['/onboarding/connect-email', 'Connect Email'],
    ['/onboarding/email-template', 'Email Template'],
    ['/onboarding/hub/install-extension', 'Install Extension'],
    ['/onboarding/hub/company-details', 'Company Details'],
    ['/onboarding/tms/company-details', 'Company Details'],
    ['/onboarding/tms/agentic', 'Set Up Numeo'],
    ['/mobile-onboarding', 'Get Started'],
    ['/mobile-onboarding/set-password', 'Set Password'],
    ['/mobile-onboarding/install', 'Install the App'],
    ['/register/from-extension', 'Sign Up'],
    ['/register/updater-lite', 'Sign Up'],

    // Public pages
    ['/tracking/:token', 'Load Tracking'],
    ['/agreements/sign/:token', 'Sign Agreement'],
    ['/load/:externalId', 'Shared Load'],
    ['/shared-load/:externalId', 'Shared Load'],
    ['/twa/load-radar', 'Load Radar'],
    ['/r/:type/:code', 'Referral'],
    ['/invite/:type/:token', 'Invitation'],
    ['/promo-accounting', 'Accounting'],
    ['/promo-dispatch', 'Dispatch'],
    ['/connect/ai-assistant', 'Connect AI Assistant'],
    ['/connect/imessage', 'Connect Messaging'],
    ['/connect/email', 'Connect Email'],
    ['/numeai-pricing', 'Pricing'],
    ['/subscribe', 'Subscribe'],
    ['/payment/success', 'Payment Successful'],
    ['/payment/cancelled', 'Payment Cancelled'],
    ['/extension/success', 'Extension Installed'],
    ['/extension/uninstall', 'Extension Uninstalled'],
    ['/m/pricing', 'Pricing'],
    ['/m/billing/success', 'Payment Successful'],
    ['/m/billing/cancel', 'Payment Cancelled'],
    ['/m/billing/return', 'Billing'],
    ['/m/*', 'Numeo Mobile App'],

    // Influencer portal
    ['/influencer/login', 'Influencer Sign In'],
    ['/influencer/dashboard', 'Influencer Dashboard'],
    ['/influencer/codes', 'Influencer Codes'],
    ['/influencer/referrals', 'Influencer Referrals'],

    // Broker portal and its admin
    ['/broker-portal/login', 'Broker Sign In'],
    ['/broker-portal/dashboard', 'Broker Dashboard'],
    ['/broker-portal/publish', 'Publish Load'],
    ['/broker-portal/publish/:externalId', 'Edit Published Load'],
    ['/broker-portal/csv-upload', 'CSV Upload'],
    ['/broker-portal/api-keys', 'API Keys'],
    ['/broker-portal/account', 'Broker Account'],
    ['/admin/broker-accounts', 'Broker Accounts'],
]

/**
 * Box Truck Profiles renames two sidebar items (`menuConfig.tsx`: Contractors → Owners, Assets → Vehicles) and their
 * page headers, so the tab follows them. Checked before `ROUTE_TITLES`, same whole-path matching.
 */
const BOX_TRUCK_PROFILES_TITLES: ReadonlyArray<readonly [pattern: string, title: string]> = [
    ['/profiles/contractors', 'Owners'],
    ['/profiles/assets/trucks', 'Vehicles'],
]

type TitleOptions = { boxTruckProfilesMode?: boolean }

const matchTitle = (table: ReadonlyArray<readonly [string, string]>, pathname: string): string | null => {
    for (const [pattern, title] of table) {
        if (matchPath({ path: pattern, end: true }, pathname)) return title
    }
    return null
}

/** The page name for a path, or null when no page is registered for it. */
export const resolveRouteTitle = (pathname: string, { boxTruckProfilesMode = false }: TitleOptions = {}): string | null =>
    (boxTruckProfilesMode ? matchTitle(BOX_TRUCK_PROFILES_TITLES, pathname) : null) ?? matchTitle(ROUTE_TITLES, pathname)

/** `Page | Numeo`; the bare `Numeo` for a path with no registered page. */
export const buildTabTitle = (pathname: string, options?: TitleOptions): string => {
    const page = resolveRouteTitle(pathname, options)
    return page ? `${page} | ${APP_TITLE}` : APP_TITLE
}
